import React, { Fragment, lazy, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames/bind';
import PropTypes from 'prop-types';
import { Observer } from 'mobx-react';
import CircularProgress from '@material-ui/core/CircularProgress';
import { MoreHoriz } from '@material-ui/icons';
import VideoIcon from '@material-ui/icons/Videocam';

import ControlledFeatureService from 'services/ControlledFeatureService';
import CookieStorageService from 'services/CookieStorageService';
import { translate } from 'services/i18nService';
import { useStores } from 'stores/StoreContextProvider';
import ACCESS_LEVEL_NUM from 'enum/accessLevelsNum';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import {
  getMasterDomain,
  isAvailableToMove,
} from 'utils/helpers';

import Hyperlink from '../../Hyperlink/Hyperlink';
import Logo from '../components/Logo';
import SideMenuTrigger from '../components/SideMenuTrigger';

import styles from './UserMenu.css';


const GenderPreference = lazy(() => import('../GenderPreference/GenderPreference'));
const Impersonate = lazy(() => import('../../Impersonate/Impersonate'));

const cx = classNames.bind(styles);

const ABUSE_APP_PATH = 'https://abuse2.cam4.com';

export default function UserMenu(props) {
  const {
    directoryActions,
    explore,
    genderPreference,
    handleSexPrefAds,
    history,
    IDS,
    initialFilters,
    isAdmin,
    isGold,
    isMinUserModerator,
    location,
    makeId,
    match,
    path,
    renderDropdownOptions,
    sfw,
    toggleMenu,
    translateOption,
    user,
    userActions,
    onBroadcastButtonClick,
  } = props;

  const [showGenderPopover, setshowGenderPopover] = useState(false);
  const { UserStore } = useStores();

  const hasPrivateDirectory = ControlledFeatureService.setUser(user).has(allUserGroups.PREMIUM_PRIVATE_SHOW);
  const horizontalMenuLink = !sfw ? styles.navLink : `${styles.navLink} ${styles.disabled}`;
  const hdCamsLinkStyle = classNames({ [styles.hdCamsLink]: !user.username, [styles.disabled]: sfw });
  const linkWrapperClass = cx({ notAllowed: sfw });
  const isGuestBilling = user.accessLevel === ACCESS_LEVEL_NUM.GUEST_BILLING;
  const isGuest = user && user.accessLevel < ACCESS_LEVEL_NUM.BASIC;
  const hideAds = isGuest && CookieStorageService.get('cam4-HideAds');
  const isLocationChangeUnavailable = !isAvailableToMove(location);
  const externalBtnClasses = classNames({
    [styles.dropdown]: true,
    [styles.hideOnSmall]: true,
    [styles.notAllowed]: sfw,
  });
  const disabledClass = classNames({ [styles.disabled]: sfw });
  const notAllowedClass = classNames({ [styles.notAllowed]: sfw });

  const disableLinkClassifSfw = cx('navLink', 'hideOnSmall', { disabled: sfw });
  const broadcastBtnClassesDropdown = cx('dropdown', 'headerElement', 'goLiveElement', 'broadcastInMenu', 'hiddenSm');
  const broadcastBtnClasses = cx('dropdown', 'headerElement', 'goLiveElement', 'hideBroadcast', 'hiddenSm');

  const { userMenuIds, userNavigationIds } = useMemo(() => {
    const { userMenuContent, navigation } = IDS;

    return {
      userMenuIds: userMenuContent(),
      userNavigationIds: navigation(),
    };
  }, [IDS]);


  const onToggleMenu = useCallback(() => {
    toggleMenu();

    if (showGenderPopover) {
      const domainName = getMasterDomain();
      CookieStorageService.set('shouldShowGenderPopover', 'hide', { path: '/', domain: domainName });
      setshowGenderPopover(false);
    }
  }, [toggleMenu, showGenderPopover]);

  const renderFanClubsLink = useCallback((className) => {
    const isFanClubsActive = UserStore.hasCF(allUserGroups.FANCLUB_DIRECTORY);

    if (!isFanClubsActive) {
      return null;
    }

    return (
      <Hyperlink
        className={classNames(styles.navLink, className)}
        id={makeId(userMenuIds.fanclubs)}
        isRouterLink={true}
        to={routeDefinitions.fanclubs}
      >
        {translate('menuoptions.menu.link.fanclubs', undefined, 'Fan Clubs')}
      </Hyperlink>
    );

  }, [UserStore, userMenuIds, makeId]);

  return (
    <div className={`${styles.navigation} ${styles.userMenu}`}>
      <div className={styles.logoWrapper}>
        <SideMenuTrigger onToggle={onToggleMenu} />
        <Logo />
      </div>

      <GenderPreference
        directoryActions={directoryActions}
        genderPreference={genderPreference}
        generatedFromUserMenu={true}
        history={history}
        initialFilters={initialFilters}
        match={match}
        path={path}
        translateOption={translateOption}
        user={user}
        userActions={userActions}
      />

      {renderFanClubsLink(styles.hideOnSmall)}

      {hasPrivateDirectory && (
        <div className={linkWrapperClass}>
          <Hyperlink
            className={disableLinkClassifSfw}
            id={makeId(userMenuIds.privateDirectory)}
            isRouterLink={true}
            to={!sfw ? routeDefinitions.privateDirectory.root : routeDefinitions.root}
          >
            {translate('directory.directory.private.nav.link')}
          </Hyperlink>
        </div>
      )}

      <div className={`${styles.dropdown} ${styles.hideOnSmall}`}>
        <button className={styles.iconArrow} id={makeId(userMenuIds.explore)}>
          {translate('menuoptions.menu.link.explore')}
        </button>

        <div className={styles.content}>
          {renderDropdownOptions(explore)}
        </div>
      </div>

      <div className={`${styles.dropdown} ${styles.moreWrap}`}>
        <div className={styles.moreButton}>
          <MoreHoriz />
        </div>

        <div className={styles.content}>
          {renderFanClubsLink()}

          {hasPrivateDirectory && (
            <div className={linkWrapperClass}>
              <Hyperlink
                className={horizontalMenuLink}
                id={makeId(userMenuIds.privateDirectory)}
                isRouterLink={true}
                to={!sfw ? routeDefinitions.privateDirectory.root : routeDefinitions.root}
              >
                {translate('directory.directory.private.nav.link')}
              </Hyperlink>
            </div>
          )}

          <div className={broadcastBtnClassesDropdown}>
            <Observer>
              {() => {
                const isDisabled = (
                  UserStore.isLoggedIn && !isGuestBilling && !UserStore.isUserConsentRequestDone
                ) || isLocationChangeUnavailable;
                const btnClasses = cx('broadcastCta', { 'broadcastCta--disabled': isDisabled });

                return (
                  <button
                    className={btnClasses}
                    disabled={isDisabled}
                    id={makeId(userNavigationIds.goLive)}
                    onClick={onBroadcastButtonClick}
                  >
                    {isDisabled ? (
                      <span className={cx('broadcastCta__icon')}>
                        <CircularProgress size={14} />
                      </span>
                    ) : (
                      <VideoIcon className={styles.icons} />
                    )}
                    <span>{translate('menuoptions.menu.link.broadcast.2')}</span>
                  </button>
                );
              }}
            </Observer>
          </div>

          {renderDropdownOptions(explore)}

          {isAdmin && (
            <Fragment>
              <Hyperlink
                className={styles.capitalized}
                href={routeDefinitions.admin.entryPage}
                id={makeId(userMenuIds.adminInNavigation)}
              >
                {translate('mail.mail.admin')}
              </Hyperlink>

              <Impersonate customStyle="inMenu" user={user} />

              <Hyperlink
                className={`${styles.adminMenu} ${styles.capitalized}`}
                href={ABUSE_APP_PATH}
                id="abuseAppButtonInDropdown"
              >
                {translate('mail.usermenu.abusebutton')}
              </Hyperlink>
            </Fragment>
          )}

          {!isGold && !hideAds && (
            <Fragment>
              <div className={notAllowedClass}>
                <Hyperlink
                  className={disabledClass}
                  href={handleSexPrefAds(genderPreference, false)}
                  id={makeId(userNavigationIds.meetAndFuckDropdown)}
                  target="popup"
                >
                  {translate('menuoptions.menu.link.find.date')}
                </Hyperlink>
              </div>
              <div className={notAllowedClass}>
                <Hyperlink
                  className={hdCamsLinkStyle}
                  href={handleSexPrefAds(genderPreference, true)}
                  id={makeId(userNavigationIds.pornGamesDropdown)}
                  target="popup"
                >
                  {translate('menuoptions.menu.link.sex.games')}
                </Hyperlink>
              </div>
            </Fragment>
          )}
        </div>
      </div>

      <div className={broadcastBtnClasses}>
        <Observer>
          {() => {
            const isDisabled = (
              UserStore.isLoggedIn && !isGuestBilling && !UserStore.isUserConsentRequestDone
            ) || isLocationChangeUnavailable;
            const btnClasses = cx('broadcastCta', { 'broadcastCta--disabled': isDisabled });

            return (
              <button
                className={btnClasses}
                disabled={isDisabled}
                id={makeId(userNavigationIds.goLive)}
                onClick={onBroadcastButtonClick}
              >
                {isDisabled ? (
                  <span className={cx('broadcastCta__icon')}>
                    <CircularProgress size={14} />
                  </span>
                ) : (
                  <VideoIcon className={styles.icons} />
                )}
                <span>{translate('menuoptions.menu.link.broadcast.2')}</span>
              </button>
            );
          }}
        </Observer>
      </div>

      {!isGold && !hideAds && (
        <div className={externalBtnClasses}>
          <Hyperlink
            className={disabledClass}
            href={handleSexPrefAds(genderPreference, false)}
            id={makeId(userNavigationIds.meetAndFuck)}
            target="popup"
          >
            <button>
              {translate('menuoptions.menu.link.find.date')}
            </button>
          </Hyperlink>
        </div>
      )}

      {!isGold && !hideAds && (
        <div className={externalBtnClasses}>
          <Hyperlink
            className={disabledClass}
            href={handleSexPrefAds(genderPreference, true)}
            id={makeId(userNavigationIds.pornGames)}
            target="popup"
          >
            <button>
              {translate('menuoptions.menu.link.sex.games')}
            </button>
          </Hyperlink>
        </div>
      )}

      {isMinUserModerator && (
        <div className={`${styles.dropdown} ${styles.hideOnSmall}`}>
          <Hyperlink
            className={`${styles.adminMenu} ${styles.capitalized}`}
            href={routeDefinitions.admin.entryPage}
            id={makeId(userMenuIds.admin)}
          >
            {translate('mail.mail.admin')}
          </Hyperlink>
        </div>
      )}

      {isAdmin && (
        <div className={`${styles.dropdown} ${styles.hideOnSmall}`}>
          <Impersonate user={user} />
        </div>
      )}

      {isMinUserModerator && (
        <div className={`${styles.dropdown} ${styles.hideOnSmall}`}>
          <Hyperlink
            className={`${styles.adminMenu} ${styles.capitalized}`}
            href={ABUSE_APP_PATH}
            id="abuseAppButton"
          >
            {translate('mail.usermenu.abusebutton')}
          </Hyperlink>
        </div>
      )}
    </div>
  );
}

UserMenu.propTypes = {
  directoryActions: PropTypes.object.isRequired,
  handleSexPrefAds: PropTypes.func.isRequired,
  makeId: PropTypes.func.isRequired,
  path: PropTypes.string.isRequired,
  renderDropdownOptions: PropTypes.func.isRequired,
  toggleMenu: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  userActions: PropTypes.object.isRequired,
  onBroadcastButtonClick: PropTypes.func.isRequired,
  IDS: PropTypes.object,
  explore: PropTypes.array,
  genderPreference: PropTypes.string,
  history: PropTypes.object,
  initialFilters: PropTypes.object,
  isAdmin: PropTypes.bool,
  isGold: PropTypes.bool,
  isMinUserModerator: PropTypes.bool,
  location: PropTypes.object,
  match: PropTypes.object,
  sfw: PropTypes.bool,
  translateOption: PropTypes.func,
};

UserMenu.defaultProps = {
  IDS: undefined,
  explore: undefined,
  genderPreference: undefined,
  history: undefined,
  initialFilters: undefined,
  isAdmin: false,
  isGold: false,
  isMinUserModerator: false,
  location: undefined,
  match: undefined,
  sfw: undefined,
  translateOption: undefined,
};
